import { action, makeObservable, observable } from "mobx";
import { ref, get } from "firebase/database";
import { database } from "../../configs";
import { SETTINGS_STORAGE_KEY } from "../../features/menu/constants";
import { getMenuContext } from "../../features/menu/helpers";
import { incrementMenuViewStat } from "../../features/menu/api";
import { DEFAULT_MAIN_COLOR } from "../../constants";
import { trackEvent } from "../../helpers";

const MENU_CACHE_KEY = "menu_cache";

export default class Menu {
  mainStore;
  storedSettings;

  data = null;
  search = "";
  isThemeDark = false;
  selectedProducts = [];
  selectedPortions = [];
  favoriteProducts = [];
  waForm = {
    delivery: {
      name: "",
      phone: "",
      address: "",
      comment: "",
    },
    takeaway: {
      name: "",
      phone: "",
      comment: "",
    },
    dineIn: {
      tableNumber: "",
      comment: "",
    },
  };

  constructor(mainStore) {
    makeObservable(this, {
      data: observable,
      setData: action,
      search: observable,
      setSearch: action,
      isThemeDark: observable,
      setIsThemeDark: action,
      selectedProducts: observable,
      setSelectedProducts: action,
      selectedPortions: observable,
      setSelectedPortions: action,
      favoriteProducts: observable,
      setFavoriteProducts: action,
      toggleFavoriteProduct: action,
      waForm: observable,
      setWAFormField: action,
      resetWAForm: action,
    });

    this.mainStore = mainStore;
    this.storedSettings = JSON.parse(
      localStorage.getItem(SETTINGS_STORAGE_KEY),
    );

    try {
      const { userID, establishmentID } = getMenuContext();
      const cacheKey = `${MENU_CACHE_KEY}_${userID}_${establishmentID}`;
      const cached = JSON.parse(localStorage.getItem(cacheKey));
      if (cached?.data) {
        this.data = cached.data;
      }
    } catch {
      window.console.warn(
        "Failed to load initial menu data from localStorage.",
      );
    }
  }

  async index(userID, establishmentID) {
    if (!userID || !establishmentID) return;

    const cacheKey = `${MENU_CACHE_KEY}_${userID}_${establishmentID}`;
    try {
      const cached = JSON.parse(localStorage.getItem(cacheKey));

      if (cached?.data) {
        this.setData(cached.data);
        this.setIsThemeDark();
      }
    } catch {
      window.console.warn(
        "Failed to load menu cache, localStorage might be corrupted.",
      );
    }

    const establishmentRef = ref(
      database,
      `${userID}/Establishments/${establishmentID}`,
    );
    const subscriptionRef = ref(database, `${userID}/PayPro/subscription`);

    try {
      const [establishmentSnapshot, subscriptionSnapshot] = await Promise.all([
        get(establishmentRef),
        get(subscriptionRef),
      ]);

      const establishmentData = establishmentSnapshot.val() || {};
      // Visitors don't need the stats subtree — keep payload and cache lean
      delete establishmentData.Statistics;
      const subscriptionData = subscriptionSnapshot.val();

      const freshData = {
        ...establishmentData,
        subscription: {
          subscription_status_name:
            subscriptionData?.subscription_status_name || null,
        },
      };

      this.setData(freshData);
      this.setIsThemeDark();

      trackEvent("menu_viewed", {
        establishment_id: establishmentID,
        establishment_user_id: userID,
        menu_language: freshData.settings?.language || "default",
        has_active_subscription:
          freshData.subscription?.subscription_status_name === "Active",
        items_count: Object.keys(freshData.Products || {}).length,
      });

      incrementMenuViewStat();

      try {
        localStorage.setItem(
          cacheKey,
          JSON.stringify({ data: freshData, timestamp: Date.now() }),
        );
      } catch {
        window.console.warn(
          "Failed to update menu cache, localStorage might be full.",
        );
      }
    } catch (error) {
      window.console.error("Error loading data:", error);
    }
  }

  setData(value) {
    this.data = value || null;
  }

  setSearch(value) {
    this.search = value || "";
  }

  setIsThemeDark(value) {
    this.isThemeDark =
      value ??
      this.storedSettings?.is_theme_dark ??
      this.data?.settings?.is_theme_dark ??
      false;
  }

  setSelectedProducts(value) {
    this.selectedProducts = value || [];
  }

  setSelectedPortions(value) {
    this.selectedPortions = value || [];
  }

  setFavoriteProducts(value) {
    this.favoriteProducts = value || [];
  }

  toggleFavoriteProduct(id) {
    if (this.isProductFavorite(id)) {
      this.favoriteProducts = this.favoriteProducts.filter(
        (favId) => favId !== id,
      );
    } else {
      this.favoriteProducts = [...this.favoriteProducts, id];
    }
  }

  isProductFavorite(id) {
    return this.favoriteProducts.includes(id);
  }

  get mainColor() {
    if (this.isThemeDark) {
      return this.data.settings?.dark_theme_main_color ?? DEFAULT_MAIN_COLOR;
    }

    return this.data.settings?.main_color ?? DEFAULT_MAIN_COLOR;
  }

  setWAFormField(method, field, value) {
    this.waForm[method][field] = value;
  }

  resetWAForm() {
    this.waForm = {
      delivery: { name: "", phone: "", address: "", comment: "" },
      takeaway: { name: "", phone: "", comment: "" },
      dineIn: { tableNumber: "", comment: "" },
    };
  }

  cleanup() {
    this.setData(null);
  }
}
